import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Accordion, AccordionSummary,
  AccordionDetails, Snackbar, Alert, CircularProgress, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import EmailIcon from '@mui/icons-material/Email';
import { adminFetch } from '../../lib/adminAuth';

const DEFAULTS = {
  verification: {
    subject: 'Verify your Rewarding account!',
    heading: 'Hi [name],',
    body: "Welcome to Rewarding — we're excited to get you started!\n\nPlease verify your email address to complete your registration:",
    buttonText: 'Verify My Account',
    footer: "This link expires in 1 hour. If you didn't create an account, you can safely ignore this email.",
  },
  welcome: {
    subject: "Welcome to Rewarding - Let's Grow Together!",
    heading: 'Hi [name],',
    body: "Welcome to Rewarding — we're thrilled to have you on board!\n\nYour business is now set up to:\n✦ Build stronger relationships with your customers\n✦ Send automated birthday and \"we miss you\" offers\n✦ Promote your specials to new local customers\n✦ Access customer behaviour insights that help you grow\n\nWhat's next?\n1. Add your business details (logo, offers, opening hours)\n2. Set up your first reward program (it takes just minutes)\n3. Relax — we'll automate the rest!",
    buttonText: 'Go to Dashboard',
    footer: "Here's to more happy customers and repeat visits.",
  },
  adminMagicLink: {
    subject: 'Rewarding Admin — Sign In',
    heading: 'Admin Sign In',
    body: 'Click the button below to sign in to the admin portal. This link expires in 5 minutes.',
    buttonText: 'Sign In to Admin Portal',
    footer: "If you didn't request this, you can safely ignore this email.",
  },
};

const EMAIL_TYPES = [
  { key: 'verification', label: 'Verification Email', description: 'Sent when a vendor registers to verify their email', color: '#1976d2' },
  { key: 'welcome', label: 'Welcome Email', description: 'Sent after a vendor completes registration', color: '#4caf50' },
  { key: 'adminMagicLink', label: 'Admin Magic Link', description: 'Sent to admin emails for sign-in', color: '#ff9800' },
];

function deepMerge(defaults, overrides) {
  const result = {};
  for (const key of Object.keys(defaults)) {
    if (typeof defaults[key] === 'object' && defaults[key] !== null && !Array.isArray(defaults[key])) {
      result[key] = deepMerge(defaults[key], overrides?.[key] || {});
    } else {
      result[key] = overrides?.[key] || defaults[key];
    }
  }
  return result;
}

export default function AdminEmails() {
  const [content, setContent] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch('/api/admin/email-content');
        const data = await res.json();
        setContent(deepMerge(DEFAULTS, data));
      } catch { /* keep defaults */ }
    })();
  }, []);

  const updateField = (type, field, value) => {
    setContent(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin/email-content', {
        method: 'PUT',
        body: JSON.stringify(content),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Email templates saved!', severity: 'success' });
      } else {
        throw new Error();
      }
    } catch {
      setSnack({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(DEFAULTS);
    setSnack({ open: true, message: 'Reset to defaults (save to apply)', severity: 'info' });
  };

  const renderPreview = (type) => {
    const t = content[type];
    const heading = t.heading.replace(/\[name\]/gi, 'Sarah');
    const body = t.body.replace(/\[name\]/gi, 'Sarah');
    return (
      <Box sx={{ mt: 2, p: 2.5, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px solid #e8e8e8' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#999', display: 'block', mb: 0.5 }}>
          Preview
        </Typography>
        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
          Subject: <strong>{t.subject}</strong>
        </Typography>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box sx={{ height: 3, bgcolor: '#ffb31b', borderRadius: 1, mb: 2 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e', mb: 1 }}>Rewarding</Typography>
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e', mb: 1 }}>{heading}</Typography>
          {body.split('\n').filter(l => l.trim()).map((line, i) => (
            <Typography key={i} variant="body2" sx={{ color: '#666', mb: 0.5 }}>{line}</Typography>
          ))}
          <Box sx={{
            display: 'inline-block', mt: 2, px: 3, py: 1.2, borderRadius: 1,
            bgcolor: type === 'adminMagicLink' ? '#1a1a2e' : '#ffb31b',
            color: type === 'adminMagicLink' ? '#fff' : '#1a1a2e',
            fontWeight: 600, fontSize: 13,
          }}>
            {t.buttonText}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#999' }}>{t.footer}</Typography>
        </Paper>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>Email Templates</Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>
            Customize the emails sent to vendors and admins. Use <Chip label="[name]" size="small" sx={{ fontSize: '0.75rem', height: 22, mx: 0.5 }} /> to insert the recipient's name.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RestoreIcon />} onClick={handleReset} size="small" sx={{ textTransform: 'none' }}>
            Reset to Defaults
          </Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving} size="small"
            sx={{ textTransform: 'none', bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </Box>
      </Box>

      {EMAIL_TYPES.map(({ key, label, description, color }) => (
        <Accordion key={key} defaultExpanded={key === 'verification'} sx={{ mb: 2, borderRadius: '12px !important', border: '1px solid #e8e8e8', boxShadow: 'none', '&::before': { display: 'none' }, overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmailIcon sx={{ color, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#222' }}>{label}</Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>{description}</Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField label="Subject" size="small" fullWidth value={content[key].subject}
                onChange={(e) => updateField(key, 'subject', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField label="Button Text" size="small" fullWidth value={content[key].buttonText}
                onChange={(e) => updateField(key, 'buttonText', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <TextField label="Heading" size="small" fullWidth value={content[key].heading}
              onChange={(e) => updateField(key, 'heading', e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Body" size="small" fullWidth multiline rows={5} value={content[key].body}
              onChange={(e) => updateField(key, 'body', e.target.value)}
              helperText="Each line becomes a paragraph. Use [name] for the recipient's name."
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Footer text" size="small" fullWidth value={content[key].footer}
              onChange={(e) => updateField(key, 'footer', e.target.value)}
              sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            {renderPreview(key)}
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
